<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import GuardianAddEditFormComponent from "@/components/models/patient/forms/GuardianAddEditFormComponent.vue";
import { useRoute, useRouter } from "vue-router";
import FormActions from "@/components/shared/form/FormActions.vue";
import { usePatient, useUpdatePatient } from "@/composables/models/patient/usePatient";
import { usePageTitle } from "@/composables/shared/usePageTitle";
import { patientSelectable } from "@/api/models/patient/patient";
import { useAbility } from "@casl/vue";
import { computed, watch } from "vue";
import { useForm } from "vee-validate";
import { useI18n } from "vue-i18n";
import { useSnackStore } from "@/stores/snackStore";
import { object, boolean } from "yup";
import { createGuardianValidationSchema } from "@/validationSchemas/models/patientValidationSchema";
import type { TGuardian } from "@/api/models/patient/patient";
import { useFormSubmitFlowLock } from "@/composables/shared/useFormSubmitFlowLock";

definePage({
  meta: {
    title: "generic.action.editEntity",
    titleParams: { entity: "generic.entity.guardian" },
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.UPDATE, $permSubjects.PATIENT__PATIENT);
    },
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const snackStore = useSnackStore();
const routeParams = route.params as { id: string };

const redirectUrl = computed(() => route.query.redirectUrl as string | undefined);
const parentPatientId = computed(
  () => redirectUrl.value?.match(/\/rooms\/patient\/([^/?#]+)/)?.[1]
);

const { data: patient, isLoading: isPatientLoading } = usePatient(
  {
    id: computed(() => routeParams.id),
  },
  { redirectOnNotFound: true }
);
const { data: parentPatient } = usePatient({
  id: parentPatientId,
  enabled: computed(() => !!parentPatientId.value),
});

usePageTitle({
  entityRef: patient,
  selectable: patientSelectable,
});

const validationSchema = computed(() =>
  object({
    guardian_data: object(createGuardianValidationSchema(t)).nullable(),
    is_payer: boolean().nullable(),
  })
);

type GuardianFormValues = {
  guardian_data: Record<string, unknown> | null;
  type?: string;
  is_payer?: boolean;
};

const { handleSubmit, setErrors, resetForm } = useForm<GuardianFormValues>({
  validationSchema,
  initialValues: {
    guardian_data: null,
  },
});

watch(
  patient,
  (p) => {
    if (!p) return;
    const relation = parentPatient.value?.guardians?.find((g) => g.guardian === routeParams.id);
    resetForm({
      values: {
        guardian_data: {
          first_name: p.first_name,
          last_name: p.last_name,
          middle_name: p.middle_name,
          preferred_name: p.preferred_name,
          sex: p.sex,
          birth_date: p.birth_date,
          language: p.language,
          language_2: p.language_2,
          picture: p.picture,
          emails: p.emails,
          phones: p.phones,
          addresses: p.addresses,
        },
        type: relation?.type,
        is_payer: relation?.is_payer ?? false,
      },
    });
  },
  { immediate: true }
);

const { mutateAsync: updatePatient } = useUpdatePatient(setErrors);
const { runSubmitFlow, isSubmitFlowPending } = useFormSubmitFlowLock();

const submit = handleSubmit((result) => {
  void runSubmitFlow(async () => {
    await updatePatient(
      {
        id: routeParams.id,
        ...(result.guardian_data ?? {}),
      },
      {
        onSuccess: async () => {
          const currentParentId = parentPatientId.value;
          const currentParent = parentPatient.value;
          const onSuccess = () => {
            snackStore.showSuccess(
              t("generic.messages.success.edit", {
                entity: t("generic.entity.guardian"),
              })
            );
            if (redirectUrl.value) {
              router.push(redirectUrl.value);
            } else {
              router.push(`/rooms/patient/${routeParams.id}`);
            }
          };
          if (currentParentId && currentParent?.guardians?.length) {
            const nextGuardians = currentParent.guardians.map((g: TGuardian) => ({
              ...g,
              type: g.guardian === routeParams.id ? result.type : g.type,
              is_payer: result.is_payer ? g.guardian === routeParams.id : g.is_payer,
              guardian_data: undefined,
            }));
            await updatePatient(
              {
                id: currentParentId,
                guardians: nextGuardians,
              },
              { onSuccess }
            );
          } else {
            onSuccess();
          }
        },
      }
    );
  });
});
</script>

<template>
  <loading v-if="isPatientLoading" />
  <template v-else>
    <v-form
      v-if="patient"
      class="tw:w-full"
      :validation-schema="validationSchema"
      @submit.prevent="submit"
      data-testid="guardian-edit-form"
    >
      <guardian-add-edit-form-component :validation-schema="validationSchema" />
      <form-actions
        :loading="isPatientLoading || isSubmitFlowPending"
        :on-save="submit"
        :on-cancel="router.back"
        save-icon="mdi-account-edit-outline"
      >
      </form-actions>
    </v-form>
  </template>
</template>

<style scoped></style>
